/**
 * The reducers are used to take actions and data and transform them into a state change
 * Each reducer is a function that takes two parameters, the current state of the redux store and the action that was triggered
 * It is the job of the reducer to take that information and return an object containing any state changes
 */
import {ACTION_TYPE} from './types'
import {MODAL_POPUP_ID} from './const'

/**
 * Helper function to create a new state object with the modifications
 * @param {{}} state The current state
 * @param {{}} modifications The modification that need to be made to the state
 * @returns {{}} a new object that represents the new state
 */
export const modifyState = (state, ...modifications) => {
	return Object.assign({}, state, ...modifications)
}

/**
 * A function that returns an object representing the current state with the needed changes
 * @callback ReducerFunction
 * @param {{}=} state The current state
 * @param {{type}=} action The action that was dispatched
 * @returns {{}} The current state with the required changes
 */

/**
 * A function that returns an object representing the needed state changes
 * @callback MappableReducerFunction
 * @param {{}=} state The current state
 * @param {{}=} action The action that was dispatched
 * @returns {{}} The required state changes that need to be added to the current state
 */

/**
 * A mapping of actions to handler functions
 * @typedef {Object} HandlerMapping
 * @property {MappableReducerFunction | MappableReducerFunction[]} ACTION_TYPE An listing of the handlers that should be invoked for the specific ACTION_TYPE
 */

/**
 * Helper function to create a reducer from a mapping of handlers
 * @param {{}} initialState The values to initialize the state
 * @param {{}|{}[]} handlerMapping The mapping of actions to handlers that will be used in this reducer
 * @returns {ReducerFunction} A function that can be used as a redux reducer
 */
export const createReducer = (initialState, handlerMapping) => {
	return (state = initialState, action) => {
		if (handlerMapping[action.type]) {
			if (Array.isArray(handlerMapping[action.type])) {
				return modifyState(state, ...(handlerMapping[action.type].map((handler) => (handler(state, action)))))
			} else {
				return modifyState(state, handlerMapping[action.type](state, action))
			}
		}
		return state
	}
}

const parseResponseBody = (action) => (JSON.parse(action.body.body))
const parseToken = (token) => (token ? (JSON.parse((new Buffer(token.split('.')[1], 'base64')).toString())) : {} )

//Case reducers for modal popup
const openModalPopup = (state, action) => ({visible: {[action.popup]: true}})
const closeModalPopup = (state, action) => ({visible: {[action.popup]: false}})
const confirmationCheck = (state, action) => {
	if (action.body.errorMessage === '[400] User is not confirmed.') {
		return openModalPopup(state, {popup: MODAL_POPUP_ID.CONFIRM_ACCOUNT})
	} else {
		return state
	}
}

//Case reducers for Login Action
const loginRequest = () => ({
	isLoginChanging: true,
	userToken: null
})

const loginSuccess = (state, action) => {
	let userToken = parseResponseBody(action)
	let isLoggedIn = !!userToken.AuthenticationResult
	let user = isLoggedIn ? parseToken(userToken.AuthenticationResult.IdToken) : {}
	return {
		isLoginChanging: false,
		userToken: userToken,
		isLoggedIn: isLoggedIn,
		user: user
	}
}

const refreshSuccess = (state, action) => {
	let userToken = parseResponseBody(action)
	let isLoggedIn = !!userToken.AuthenticationResult
	let user = isLoggedIn ? parseToken(userToken.AuthenticationResult.IdToken) : {}
	return {
		isLoginChanging: false,
		userToken: userToken,
		isLoggedIn: isLoggedIn,
		user: user
	}
}

const clearLogin = (/*state, action*/) => ({
	isLoginChanging: false,
	userToken: null,
	isLoggedIn: false,
	user: null
})

const clearUsername = () => ({username: ''})

const clearPassword = () => ({password: ''})

const clearEmail = () => ({email: ''})

const clearConfirmationCode = () => ({confirmationCode: ''})

const clearDomains = () => ({domains: []})

const clearResources = () => ({resources: []})

const resetAddDomainForm = () => ({domain: ''})

const resetAddDomainResourceForm = () => ({resourceUrl: ''})

const setError = (state, action) => ({err: action.body})

const clearError = () => ({err: null})

const disable = () => ({disabled: true})

const enable = () => ({disabled: false})

const changePassword = (state, action) => ({password: action.password})

const changeUsername = (state, action) => ({username: action.username})

const changeEmail = (state, action) => ({email: action.email})

const changeConfirmationCode = (state, action) => ({confirmationCode: action.confirmationCode})

const changeDomain = (state, action) => ({domain: action.domain})

const changeResourceUrl = (state, action) => ({resourceUrl: action.resourceUrl})

const setResources = (state, action) => {
  const resources = parseResponseBody(action).resources.map((resource) => {
    resource.Busy = false;
    resource.Error = false;
    return resource
  })

  return ({ resources: resources })
}

const addResource = (state, action) => {
	const resources = state.resources.slice();
	resources.push(parseResponseBody(action))
	return ({resources: resources})
}

const resourceEditRequest = (state, action) => {
  const resources = state.resources.slice();
  const index = resources.findIndex((resource) => {
    return resource.URL === action.request.URL
  })
  if (index > -1) {
    resources[index].Busy = true;
  }
  return ({ resources: resources })
}

const resourceEditError = (state, action) => {
  const resources = state.resources.slice();
  const index = resources.findIndex((resource) => {
    return resource.URL === action.request.URL
  })
  if (index > -1) {
    resources[index].Busy = false;
    resources[index].Error = true;
  }
  return ({ resources: resources })
}

const delResource = (state, action) => {
  const body = parseResponseBody(action);
  const resources = state.resources.slice();
  // Find the resources index
  const index = resources.findIndex((resource) => {
    return resource.Owner === body.Owner && resource.URL === body.URL
  })
  if (index > -1) {
    // Remove the resource from the array
    resources.splice(index, 1);
  }
  return ({ resources: resources })
}

const addDomain = (state, action) => {
	const body = parseResponseBody(action)
	if (body.domain) {
		const domains = state.domains.slice();
		domains.push(body.domain)
		return ({domains: domains})
	} else {
		return state;
	}
}

const toggleDomainEdit = (state, action) => {
  // Clear resource error states
  const resources = state.resources.map((resource) => {
    resource.Error = false
    return resource
  });
  
  if (state.editableDomain === action.domain) {
    return { editableDomain: '' }
  }
  return { resources, editableDomain: action.domain }
}

const parseDomains = (state, action) => {
	const userToken = parseResponseBody(action)
	const isLoggedIn = !!userToken.AuthenticationResult
	const idTokenData = isLoggedIn ? parseToken(userToken.AuthenticationResult.IdToken) : {}
	const domains = (isLoggedIn && idTokenData['custom:domains']) ? JSON.parse(idTokenData['custom:domains']) : []
	return {domains: domains}
}

//Reducer configuration
const modalPopupConfig = {
	[ACTION_TYPE.OPEN_MODAL_POPUP]: openModalPopup,
	[ACTION_TYPE.CLOSE_MODAL_POPUP]: closeModalPopup,
	[ACTION_TYPE.LOGIN_RESPONSE]: (state) => (closeModalPopup(state, {popup: MODAL_POPUP_ID.LOGIN})),
	[ACTION_TYPE.ADD_DOMAIN_RESPONSE]: (state) => (closeModalPopup(state, {popup: MODAL_POPUP_ID.ADD_DOMAIN})),
	[ACTION_TYPE.CONFIRM_SIGNUP_RESPONSE]: [
		(state) => (closeModalPopup(state, {popup: MODAL_POPUP_ID.CONFIRM_ACCOUNT})),
		(state) => (openModalPopup(state, {popup: MODAL_POPUP_ID.LOGIN}))],
	[ACTION_TYPE.SIGNUP_RESPONSE]: [
		(state) => (closeModalPopup(state, {popup: MODAL_POPUP_ID.SIGN_UP})),
		(state) => (openModalPopup(state, {popup: MODAL_POPUP_ID.CONFIRM_ACCOUNT}))],
	[ACTION_TYPE.LOGIN_RESPONSE_FAIL]: confirmationCheck,
}

const userInfoConfig = {
	[ACTION_TYPE.USERNAME_CHANGED]: changeUsername,
	[ACTION_TYPE.PASSWORD_CHANGED]: changePassword,
	[ACTION_TYPE.EMAIL_CHANGED]: changeEmail,
	[ACTION_TYPE.CONFIRMATION_CODE_CHANGED]: changeConfirmationCode,
	[ACTION_TYPE.LOGIN_RESPONSE]: [clearPassword],
	[ACTION_TYPE.LOGOUT]: [clearUsername, clearPassword, clearConfirmationCode, clearEmail],
	[ACTION_TYPE.CONFIRM_SIGNUP_RESPONSE]: clearConfirmationCode,
	[ACTION_TYPE.SIGNUP_RESPONSE]: clearEmail,
}

const loginReducerConfig = {
	[ACTION_TYPE.LOGIN_REQUEST]: loginRequest,
	[ACTION_TYPE.LOGIN_RESPONSE]: loginSuccess,
	[ACTION_TYPE.REFRESH_RESPONSE]: refreshSuccess,
	[ACTION_TYPE.LOGOUT]: clearLogin,
}

const loginFormReducerConfig = {
	[ACTION_TYPE.LOGIN_REQUEST]: [disable, clearError],
	[ACTION_TYPE.LOGIN_RESPONSE]: enable,
	[ACTION_TYPE.LOGIN_RESPONSE_FAIL]: [enable, setError],
}

const signUpFormReducerConfig = {
	[ACTION_TYPE.SIGNUP_REQUEST]: [disable, clearError],
	[ACTION_TYPE.SIGNUP_RESPONSE]: enable,
	[ACTION_TYPE.SIGNUP_RESPONSE_FAIL]: [enable, setError],
}

const confirmationFormReducerConfig = {
	[ACTION_TYPE.CONFIRM_SIGNUP_REQUEST]: [disable, clearError],
	[ACTION_TYPE.CONFIRM_SIGNUP_RESPONSE]: enable,
	[ACTION_TYPE.CONFIRM_SIGNUP_RESPONSE_FAIL]: [enable, setError],
}

const addDomainFormReducerConfig = {
	[ACTION_TYPE.DOMAIN_CHANGED]: changeDomain,
	[ACTION_TYPE.ADD_DOMAIN_REQUEST]: [disable, clearError],
	[ACTION_TYPE.ADD_DOMAIN_RESPONSE]: [enable, resetAddDomainForm],
	[ACTION_TYPE.ADD_DOMAIN_RESPONSE_FAIL]: [enable, setError],
}

const addDomainResourceFormReducerConfig = {
	[ACTION_TYPE.RESOURCE_URL_CHANGED]: changeResourceUrl,
	[ACTION_TYPE.ADD_DOMAIN_RESOURCE_REQUEST]: [disable, clearError],
	[ACTION_TYPE.ADD_DOMAIN_RESOURCE_RESPONSE]: [enable, resetAddDomainResourceForm],
	[ACTION_TYPE.ADD_DOMAIN_RESOURCE_RESPONSE_FAIL]: [enable, setError],
}
 
const siteDataReducerConfig = {
	[ACTION_TYPE.LOGIN_RESPONSE]: parseDomains,
	[ACTION_TYPE.GET_RESOURCES_RESPONSE]: setResources,
	[ACTION_TYPE.ADD_DOMAIN_RESOURCE_RESPONSE]: addResource,
  [ACTION_TYPE.DEL_DOMAIN_RESOURCE_RESPONSE]: delResource,
  [ACTION_TYPE.DEL_DOMAIN_RESOURCE_REQUEST]: resourceEditRequest,
  [ACTION_TYPE.DEL_DOMAIN_RESOURCE_RESPONSE_FAIL]: resourceEditError,
	[ACTION_TYPE.ADD_DOMAIN_RESPONSE]: addDomain,
  [ACTION_TYPE.EDIT_DOMAIN_TOGGLE]: toggleDomainEdit,
	[ACTION_TYPE.LOGOUT]: [clearDomains, clearResources],
}

const accountDataReducerConfig = {}

const paymentMethodReducerConfig = {
    [ACTION_TYPE.GET_PAYMENT_TOKEN_REQUEST]: [disable, clearError],
    [ACTION_TYPE.GET_PAYMENT_TOKEN_RESPONSE]: [enable],
    [ACTION_TYPE.GET_PAYMENT_TOKEN_RESPONSE_FAIL]: [enable, setError],
    [ACTION_TYPE.ADD_PAYMENT_METHOD_REQUEST]: [disable, clearError],
    [ACTION_TYPE.ADD_PAYMENT_METHOD_RESPONSE]: [enable],
    [ACTION_TYPE.ADD_PAYMENT_METHOD_RESPONSE_FAIL]: [enable, setError],
}

//Reducers created from configuration
export const userInfo = createReducer({username: '', password: '', email: '', confirmationCode: ''}, userInfoConfig)
export const login = createReducer({isLoggedIn: false}, loginReducerConfig)
export const loginForm = createReducer({disabled: false}, loginFormReducerConfig)
export const signUpForm = createReducer({disabled: false}, signUpFormReducerConfig)
export const confirmationForm = createReducer({disabled: false}, confirmationFormReducerConfig)
export const addDomainForm = createReducer({domain: '', disabled: false}, addDomainFormReducerConfig)
export const addDomainResourceForm = createReducer({resourceUrl: '', disabled: false}, addDomainResourceFormReducerConfig)
export const modalPopup = createReducer({visible: {}}, modalPopupConfig)
export const siteData = createReducer({ resources: [], domains: [], editableDomain: '' },
  siteDataReducerConfig)
export const accountData = createReducer({disabled: false}, accountDataReducerConfig)
export const paymentMethod = createReducer({disabled: false}, paymentMethodReducerConfig)



// WEBPACK FOOTER //
// ./src/redux/reducers.js